import React, { Suspense, useEffect, useState } from 'react';
import { lazyWithReload } from './utils/lazyWithReload';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DashboardProvider, useDashboard } from './context/DashboardContext';
import { AdminProvider, useAdmin } from './context/AdminContext';
import { useTheme } from './hooks/useTheme';
import { FeatureRoute } from './components/FeatureRoute';
import FloatingNotificationsContainer from './components/FloatingNotificationsContainer';
import { RootErrorBoundary } from './components/RootErrorBoundary';
import { CheckoutReturnHandler } from './components/billing/CheckoutReturnHandler';
import { ActiveEnterpriseSync } from './components/billing/ActiveEnterpriseSync';
import { TrialExpiredScreen, isAccessLocked } from './components/billing/TrialExpiredScreen';

// Eager-load only the auth screens (visited before the shell renders)
import Login         from './pages/Login';
import ResetPassword from './pages/ResetPassword';

// Route-level code splitting — each page becomes its own JS chunk
const MenuPB2B         = lazyWithReload(() => import('./pages/MenuPB2B'));
const JobDescriptions  = lazyWithReload(() => import('./pages/JobDescriptions'));
const Pipeline         = lazyWithReload(() => import('./pages/Pipeline'));
// FollowUp page kept as backup — route disabled, logic now lives in Pipeline as FollowUpColumn
const AdvancedSearch   = lazyWithReload(() => import('./pages/AdvancedSearch'));
// Labo de comparaison des moteurs de recherche (aqcand / hybride FE+PDL / PDL seul).
// Réservé aux super-admins TrueCalling : la page dépense des crédits de sourcing.
const LabsCompareSearch = lazyWithReload(() => import('./pages/LabsCompareSearch'));
const RecherchePdl     = lazyWithReload(() => import('./pages/RecherchePdl'));
const VivierSearch     = lazyWithReload(() => import('./pages/VivierSearch'));
const PhotoCompare     = lazyWithReload(() => import('./pages/PhotoCompare'));
const CVLibrary        = lazyWithReload(() => import('./pages/CVLibrary'));
const OutPlacement     = lazyWithReload(() => import('./pages/OutPlacement'));
const Historique       = lazyWithReload(() => import('./pages/Historique'));
const InPlacement      = lazyWithReload(() => import('./pages/InPlacement'));
const GoogleJobsSearch = lazyWithReload(() => import('./pages/GoogleJobsSearch'));
const Settings         = lazyWithReload(() => import('./pages/Settings'));
const Submissions      = lazyWithReload(() => import('./pages/Submissions'));
const FastScoring      = lazyWithReload(() => import('./pages/FastScoring'));
const StubPage         = lazyWithReload(() => import('./pages/StubPage'));
const CandidateChat    = lazyWithReload(() => import('./pages/CandidateChat'));
const Conversations    = lazyWithReload(() => import('./pages/Conversations'));
const Admin            = lazyWithReload(() => import('./pages/Admin'));
const AdminAnalytics   = lazyWithReload(() => import('./pages/AdminAnalytics'));
const DeelAnalytics    = lazyWithReload(() => import('./pages/DeelAnalytics'));
const AdminSecurity    = lazyWithReload(() => import('./pages/AdminSecurity'));
const InterviewPreparation = lazyWithReload(() => import('./pages/InterviewPreparation'));
const WriterBotLayout = lazyWithReload(() => import('./components/WriterBot/WriterBotLayout'));
const BotCommandCenter = lazyWithReload(() => import('./pages/BotCommandCenter'));
const GenialeadsPage = lazyWithReload(() => import('./pages/genialeads'));
const NewGenialeadsPage = lazyWithReload(() => import('./pages/genialeads-new'));
const EmilyWebinarRecruiter = lazyWithReload(() => import('./pages/EmilyWebinarRecruiter'));
const EmilyWebinarCandidate = lazyWithReload(() => import('./pages/EmilyWebinarCandidate'));
const DeelOAuthCallback = lazyWithReload(() => import('./pages/DeelOAuthCallback'));
const IntegrationOAuthCallback = lazyWithReload(() => import('./pages/IntegrationOAuthCallback'));
const JobPublicPage = lazyWithReload(() => import('./pages/JobPublicPage'));
const PsychometricTest = lazyWithReload(() => import('./pages/PsychometricTest'));
const OAuthConsent = lazyWithReload(() => import('./pages/OAuthConsent'));
// Inscription publique (lien direct du site vitrine, forfait pré-sélectionné) + retour de paiement.
const Signup = lazyWithReload(() => import('./pages/Signup'));
const SignupThanks = lazyWithReload(() => import('./pages/SignupThanks'));

const PAGE_TITLES: Record<string, string> = {
  '/menupb2b':      'Dashboard — TrueCalling',
  '/jdv1':          'Job Descriptions — TrueCalling',
  '/job4manyv2':    'Pipeline — TrueCalling',
  '/interview-prep': 'Interview Preparation — TrueCalling',
  '/aqcand':        'Advanced Search — TrueCalling',
  '/cvwa':          'CV Library — TrueCalling',
  '/outplacement':  'Outplacement — TrueCalling',
  '/historique':    'Historique — TrueCalling',
  '/inplacement':   'Inplacement — TrueCalling',
  '/googlejobs':    'Google Jobs — TrueCalling',
  '/settings':      'Settings — TrueCalling',
  '/submissions':   'Submissions — TrueCalling',
  '/fastscoring':   'Fast Scoring — TrueCalling',
  '/conversations': 'Conversations — TrueCalling',
  '/admin':         'Admin — TrueCalling',
  '/admin/analytics': 'Analytics — TrueCalling',
  '/labs/compare':  'Labo recherche — TrueCalling',
  '/deel/analytics': 'Deel — Traçabilité',
  '/writer-bot':    'Writer Bot — TrueCalling',
  '/bot-command-center': 'Bot Command Center — TrueCalling',
  '/genialeads':    'Genialeads — TrueCalling',
  '/genialeads/new': 'New Campaign — TrueCalling',
  '/login':         'Login — TrueCalling',
};

const PageTitleSync: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = PAGE_TITLES[pathname] ?? 'TrueCalling';
  }, [pathname]);
  return null;
};

const LanguageSync: React.FC = () => {
  const { language } = useDashboard();
  const RTL_LANGS = ['ar', 'he'];

  useEffect(() => {
    const htmlEl = document.documentElement;
    htmlEl.lang = language;
    htmlEl.dir = RTL_LANGS.includes(language) ? 'rtl' : 'ltr';
  }, [language]);

  return null;
};

const ThemeSync: React.FC = () => {
  const { theme } = useTheme();
  useEffect(() => { document.documentElement.dataset.theme = theme; }, [theme]);
  return null;
};

const PageLoader: React.FC = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100dvh', color: 'var(--gray-400)', fontSize: 14,
  }}>
    Loading…
  </div>
);

// #20 : essai Free terminé (le SERVEUR rend access_mode 'locked') → l'écran « choisir une
// formule » remplace TOUTE page privée (aucun menu) ; super-admin jamais bloqué (isAccessLocked).
// Exporté pour le test de branchement (src/App.test.tsx) : la garde RÉELLE est rendue, pas l'écran seul.
export const PrivateRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading, activeAccess } = useAuth();
  if (loading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', color: 'var(--gray-400)', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }
  if (!user.loggedIn) return <Navigate to="/login" replace />;
  if (user.needsPasswordReset) return <Navigate to="/reset-password" replace />;
  if (isAccessLocked(user, activeAccess)) return <TrialExpiredScreen />;
  return element;
};

// Routes d'une feature de forfait : <FeatureRoute> (components/FeatureRoute.tsx, #23) — affiche
// « non inclus dans votre forfait » à la place de la page ; table route → feature : utils/navFeatures.ts.

const AdminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading: authLoading, activeAccess } = useAuth();
  const { adminRole, loading: adminLoading } = useAdmin();
  if (authLoading || adminLoading) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', color: 'var(--gray-400)', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }
  if (!user.loggedIn) return <Navigate to="/login" replace />;
  if (isAccessLocked(user, activeAccess)) return <TrialExpiredScreen />;
  if (adminRole === null) return <Navigate to="/menupb2b" replace />;
  return element;
};

// Strictement réservé aux super-admins TrueCalling (tc_admin). On gate sur la
// SOURCE DE VÉRITÉ SERVEUR api.is_tc_admin() (= appartenance à public.tc_admins),
// pas sur l'heuristique client adminRole (qui inclut role 'admin' entreprise + une
// allowlist d'emails bundlée). Ainsi le garde client == le garde serveur des RPC :
// un adm_company / admin entreprise / user normal est redirigé, jamais de page cassée.
const SuperAdminRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { user, loading: authLoading } = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    let alive = true;
    if (!user.loggedIn) { setAllowed(false); return; }
    setAllowed(null);
    (async () => {
      try {
        const { data, error } = await supabase.rpc('is_tc_admin');
        if (alive) setAllowed(!error && data === true);
      } catch {
        if (alive) setAllowed(false);
      }
    })();
    return () => { alive = false; };
  }, [user.loggedIn]);

  if (authLoading || (user.loggedIn && allowed === null)) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100dvh', color: 'var(--gray-400)', fontSize: 14,
      }}>
        Loading…
      </div>
    );
  }
  if (!user.loggedIn) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/menupb2b" replace />;
  return element;
};

const AppContent: React.FC = () => {
  const { user } = useAuth();

  return (
    <>
      <PageTitleSync />
      <LanguageSync />
      <ThemeSync />
      {/* Retour Stripe : confirme le paiement, relit le forfait, déclenche la reprise de l'action payée. */}
      <CheckoutReturnHandler />
      {/* Limites du forfait = entreprise sélectionnée (utilisateur de plusieurs entreprises). */}
      <ActiveEnterpriseSync />
      <FloatingNotificationsContainer
        enterpriseId={user?.enterprise_id || null}
        userId={user?.userId}
      />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/"               element={<Navigate to="/menupb2b" replace />} />
          <Route path="/login"          element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/menupb2b"       element={<PrivateRoute element={<MenuPB2B />} />} />
          <Route path="/jdv1"           element={<PrivateRoute element={<JobDescriptions />} />} />
          <Route path="/job4manyv2"     element={<PrivateRoute element={<Pipeline />} />} />
          <Route path="/interview-prep/:candidateId" element={<PrivateRoute element={<InterviewPreparation />} />} />
          <Route path="/selectionfu"    element={<Navigate to="/job4manyv2" replace />} />
          <Route path="/aqcand"         element={<PrivateRoute element={<AdvancedSearch />} />} />
          {/* Page d'essai : MÊME écran, nouvelle recherche (voies + pré-classement). N'a d'effet que là où
              REACT_APP_SEARCH_LANES est levé ; ailleurs elle se comporte exactement comme /aqcand. */}
          {/* Page d'essai du 17-18/09/2026, unifiée dans /aqcand : les liens gardés redirigent. */}
          <Route path="/aqcand2"        element={<Navigate to="/aqcand" replace />} />
          <Route path="/recherche-pdl"  element={<PrivateRoute element={<RecherchePdl />} />} />
          <Route path="/vivier"       element={<FeatureRoute feature="vivier_pdl" element={<VivierSearch />} />} />
          {/* Banc d'essai photo : réservé au QA super-admin. Chaque lancement dépense des
              appels Proxycurl/SERP facturés — il n'a jamais eu vocation à être ouvert à
              tout compte connecté. Entrée unique : Administration › Rapports QA. */}
          <Route path="/photo-compare" element={<SuperAdminRoute element={<PhotoCompare />} />} />
          <Route path="/labs/compare"   element={<SuperAdminRoute element={<LabsCompareSearch />} />} />
          <Route path="/cvwa"           element={<PrivateRoute element={<CVLibrary />} />} />
          <Route path="/companyjdsearch" element={<Navigate to="/outplacement" replace />} />
          <Route path="/outplacement"   element={<FeatureRoute feature="outplacement" element={<OutPlacement />} />} />
          <Route path="/historique"     element={<FeatureRoute feature="outplacement" element={<Historique />} />} />
          <Route path="/inplacement"    element={<PrivateRoute element={<InPlacement />} />} />
          <Route path="/googlejobs"     element={<PrivateRoute element={<GoogleJobsSearch />} />} />
          <Route path="/settings"       element={<PrivateRoute element={<Settings />} />} />
          <Route path="/submissions"    element={<FeatureRoute feature="psychometric" element={<Submissions />} />} />
          <Route path="/fastscoring"    element={<PrivateRoute element={<FastScoring />} />} />
          <Route path="/conversations"  element={<PrivateRoute element={<Conversations />} />} />
          <Route path="/writer-bot"     element={<SuperAdminRoute element={<WriterBotLayout />} />} />
          <Route path="/bot-command-center" element={<SuperAdminRoute element={<BotCommandCenter />} />} />
          <Route path="/genialeads"     element={<PrivateRoute element={<GenialeadsPage />} />} />
          <Route path="/genialeads/new" element={<PrivateRoute element={<NewGenialeadsPage />} />} />
          <Route path="/webinar/:callCode"   element={<PrivateRoute element={<EmilyWebinarRecruiter />} />} />
          <Route path="/auth/deel/callback" element={<PrivateRoute element={<DeelOAuthCallback />} />} />
          <Route path="/auth/integration/callback" element={<PrivateRoute element={<IntegrationOAuthCallback />} />} />
          {/* Routes publiques — pas de login requis */}
          <Route path="/oauth/consent"  element={<OAuthConsent />} />
          <Route path="/jobs/:id"       element={<JobPublicPage />} />
          {/* Inscription indépendante du login : ?plan=&period=&region=&seats=&lang= pré-sélectionnés. */}
          <Route path="/signup"         element={<Signup />} />
          <Route path="/signup/merci"   element={<SignupThanks />} />
          <Route path="/test/:token"    element={<PsychometricTest />} />
          <Route path="/admin"          element={<AdminRoute element={<Admin />} />} />
          <Route path="/admin/analytics" element={<AdminRoute element={<AdminAnalytics />} />} />
          <Route path="/deel/analytics" element={<FeatureRoute feature="deel" element={<DeelAnalytics />} />} />
          <Route path="/admin/security"  element={<SuperAdminRoute element={<AdminSecurity />} />} />
          <Route path="/chat/:token"    element={<CandidateChat />} />
          <Route path="/screening/:callCode" element={<EmilyWebinarCandidate />} />
          <Route path="*"               element={<StubPage title="Page introuvable" route="*" />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <DashboardProvider>
      <AdminProvider>
        <BrowserRouter>
          <RootErrorBoundary>
            <AppContent />
          </RootErrorBoundary>
        </BrowserRouter>
      </AdminProvider>
    </DashboardProvider>
  </AuthProvider>
);

export default App;
